// ---- HUD headshots ----
// One-shot offscreen WebGL portraits for the focus target card AND the
// bottom-right player card. Builds the REAL mesh (same factories as the
// world), frames the head (or upper body when there's no head limb), and
// caches a 2D bitmap so re-paint is free. Own GL context — never touches the
// game's composer.

import * as THREE from "three";
import { buildNpc, type NpcSpec } from "./npc/define.ts";
import { dressNpc } from "./npc/dress.ts";
import { buildPlayer } from "./player.ts";
import { disposeGroup } from "./meshkit.ts";
import type { Appearance, Gear } from "./shared.ts";

const SIZE = 96;
const FOV = 28;

let renderer: THREE.WebGLRenderer | null = null;
let scene: THREE.Scene | null = null;
let camera: THREE.PerspectiveCamera | null = null;
let lightsReady = false;

/** Cache key → painted square canvas (ready to blit onto the HUD). */
const cache = new Map<string, HTMLCanvasElement>();

function ensureGl(): {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
} {
  if (renderer && scene && camera) return { renderer, scene, camera };

  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: true, // read pixels after render for the 2D cache
  });
  renderer.setPixelRatio(1);
  renderer.setSize(SIZE, SIZE, false);
  renderer.setClearColor(0x000000, 0);
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(FOV, 1, 0.05, 40);

  if (!lightsReady) {
    scene.add(new THREE.HemisphereLight(0xbcd7ff, 0x5a4a32, 0.75));
    const key = new THREE.DirectionalLight(0xfff2d6, 1.65);
    key.position.set(1.8, 3.4, 2.4);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xa8c4e8, 0.4);
    fill.position.set(-2.2, 1.4, 1.6);
    scene.add(fill);
    scene.add(new THREE.AmbientLight(0xfff6e8, 0.28));
    lightsReady = true;
  }

  return { renderer, scene, camera };
}

/** Prefer the rig's head limb; otherwise the upper ~45% of the full AABB so
 *  animals/parts models still read as a "bust" instead of a tiny full-body. */
function portraitBox(group: THREE.Object3D): THREE.Box3 {
  let head: THREE.Object3D | null = null;
  group.traverse((o) => {
    if (o.userData?.limb === "head") head = o;
  });
  const box = new THREE.Box3();
  if (head) {
    box.setFromObject(head);
    // A touch of shoulder/neck so the crop doesn't float as a severed head.
    const size = box.getSize(new THREE.Vector3());
    box.min.y -= size.y * 0.35;
    box.expandByScalar(Math.max(size.x, size.y, size.z) * 0.12);
    return box;
  }
  box.setFromObject(group);
  const size = box.getSize(new THREE.Vector3());
  if (size.y > 0.01) box.min.y = box.max.y - size.y * 0.48;
  return box;
}

function framePortrait(
  group: THREE.Object3D,
  cam: THREE.PerspectiveCamera,
): void {
  group.updateMatrixWorld(true);
  const box = portraitBox(group);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const r = Math.max(size.x, size.y, size.z, 0.2) * 0.5;
  const dist = (r / Math.sin((FOV * Math.PI) / 360)) * 1.2;
  // Slight ¾ view so the face reads better than dead-on.
  cam.position.set(center.x + dist * 0.28, center.y + dist * 0.04, center.z + dist);
  cam.lookAt(center);
  cam.near = Math.max(0.02, dist * 0.05);
  cam.far = dist * 6;
  cam.updateProjectionMatrix();
}

/** Render `group` (feet at origin) to a cached-size canvas, then dispose it. */
function renderGroupHeadshot(group: THREE.Group): HTMLCanvasElement | null {
  const gl = ensureGl();
  group.position.set(0, 0, 0);
  group.rotation.set(0, 0, 0);
  group.scale.setScalar(1);
  gl.scene.add(group);

  framePortrait(group, gl.camera);
  gl.renderer.render(gl.scene, gl.camera);

  const out = document.createElement("canvas");
  out.width = SIZE;
  out.height = SIZE;
  const ctx = out.getContext("2d");
  if (ctx) ctx.drawImage(gl.renderer.domElement, 0, 0);

  gl.scene.remove(group);
  disposeGroup(group);
  return ctx ? out : null;
}

function blit(dest: HTMLCanvasElement, baked: HTMLCanvasElement): void {
  const ctx = dest.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, dest.width, dest.height);
  ctx.drawImage(baked, 0, 0, dest.width, dest.height);
}

function cacheKeyNpc(spec: NpcSpec): string {
  // Kind is the content doc id — every goblin spawn shares one headshot.
  return `npc:${spec.kind ?? spec.model ?? spec.id}`;
}

function cacheKeyPlayer(look: Appearance, gear: Gear): string {
  // Appearance + worn body gear (held weapons are mostly cropped by framing).
  return `player:${JSON.stringify({ look, gear })}`;
}

/** Paint a headshot of an NPC `spec` into `dest`. Cached per kind. */
export function paintFocusPortrait(dest: HTMLCanvasElement, spec: NpcSpec): void {
  const key = cacheKeyNpc(spec);
  let baked = cache.get(key);
  if (!baked) {
    const portraitSpec: NpcSpec = { ...spec, x: 0, y: 0, z: 0, facing: 0 };
    const { group } = buildNpc(portraitSpec);
    try { dressNpc(group, portraitSpec); } catch { /* dress is best-effort */ }
    baked = renderGroupHeadshot(group) ?? undefined;
    if (!baked) return;
    cache.set(key, baked);
  }
  blit(dest, baked);
}

/** Paint a headshot of the local hero into `dest`. Cached per look+gear. */
export function paintPlayerPortrait(
  dest: HTMLCanvasElement,
  look: Appearance,
  gear: Gear = {},
): void {
  const key = cacheKeyPlayer(look, gear);
  let baked = cache.get(key);
  if (!baked) {
    try {
      const group = buildPlayer(look, gear);
      baked = renderGroupHeadshot(group) ?? undefined;
    } catch {
      // Content not ready yet (pre-boot) — leave dest as-is.
      return;
    }
    if (!baked) return;
    cache.set(key, baked);
  }
  blit(dest, baked);
}

/** Drop cached bitmaps (map swap / hot reload). GL context stays warm. */
export function clearFocusPortraitCache(): void {
  cache.clear();
}
